import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Upload } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/layouts/AdminLayout";
import { FormDialog } from "@/components/FormDialog";
import { ChecklistEditor } from "@/components/settings/ChecklistEditor";
import { ZonesTab } from "@/components/settings/ZonesTab";
import { PrepSettingsTab } from "@/components/settings/PrepSettingsTab";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fileUrl } from "@/lib/api";
import { pick } from "@/lib/forms";

const TENANT_FIELDS = [["name", "Nombre comercial"], ["legal_name", "Razón social"], ["phone", "Teléfono"], ["email", "Correo"], ["website", "Sitio web"], ["address", "Dirección"], ["folio_prefix", "Prefijo de folio"]];

const TenantTab = () => {
  const { loadMeta } = useAuth();
  const [t, setT] = useState(null);
  const [logoKey, setLogoKey] = useState(Date.now());
  const ref = useRef(null);
  useEffect(() => { api.get("/settings/tenant").then((r) => setT(r.data)); }, []);
  if (!t) return null;
  const save = async () => {
    try {
      await api.patch("/settings/tenant", Object.fromEntries(TENANT_FIELDS.map(([k]) => [k, t[k] || ""])));
      await loadMeta();
      toast.success("Datos de la empresa guardados");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/tenant/logo", fd);
      setT({ ...t, has_logo: true });
      setLogoKey(Date.now());
      toast.success("Logo actualizado");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid max-w-3xl gap-6 lg:grid-cols-[1fr_220px]">
      <div className="grid gap-4 sm:grid-cols-2">
        {TENANT_FIELDS.map(([k, label]) => (
          <div key={k} className={`space-y-1.5 ${k === "address" ? "sm:col-span-2" : ""}`}>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
            <Input value={t[k] || ""} onChange={(e) => setT({ ...t, [k]: e.target.value })} data-testid={`tenant-${k}-input`} />
          </div>
        ))}
        <div className="sm:col-span-2"><Button onClick={save} data-testid="tenant-save-button">Guardar</Button></div>
      </div>
      <div className="space-y-3">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Logo para PDF</Label>
        <div className="flex h-28 items-center justify-center rounded-lg border border-dashed border-border bg-white/95 p-3">
          {t.has_logo ? <img src={`${fileUrl("/files/logo")}&k=${logoKey}`} alt="Logo" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-500">Sin logo</span>}
        </div>
        <Button variant="outline" className="w-full" onClick={() => ref.current?.click()} data-testid="tenant-logo-upload-button"><Upload className="mr-2 h-4 w-4" />Subir logo</Button>
        <input ref={ref} type="file" accept="image/png,image/jpeg" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="tenant-logo-input" />
      </div>
    </div>
  );
};

const CrudTable = ({ title, endpoint, fields, columns, testid, onChange }) => {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get(endpoint).then((r) => setItems(r.data)), [endpoint]);
  useEffect(() => { load(); }, [load]);
  const save = async (form) => {
    if (dlg?.item) await api.patch(`${endpoint}/${dlg.item.id}`, form);
    else await api.post(endpoint, form);
    toast.success("Guardado");
    await load();
    onChange?.();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        <Button size="sm" onClick={() => setDlg({})} data-testid={`${testid}-new-button`}><Plus className="mr-1 h-3.5 w-3.5" />Agregar</Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-card/30">
        <Table data-testid={`${testid}-table`}>
          <TableHeader><TableRow className="hover:bg-transparent">{columns.map((c) => <TableHead key={c.label} className="font-mono text-[11px] uppercase tracking-wider">{c.label}</TableHead>)}<TableHead /></TableRow></TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`${testid}-row-${it.id}`}>
                {columns.map((c) => <TableCell key={c.label} className="text-sm">{c.render(it)}</TableCell>)}
                <TableCell><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDlg({ item: it })} data-testid={`${testid}-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <FormDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} title={dlg?.item ? `Editar · ${title}` : `Agregar · ${title}`} fields={fields}
        initial={dlg?.item ? pick(dlg.item, fields) : null} onSubmit={save} testid={`${testid}-form`} />
    </div>
  );
};

const useLookups = () => {
  const [bus, setBus] = useState([]);
  const [sts, setSts] = useState([]);
  const reload = useCallback(() => {
    api.get("/settings/business-units").then((r) => setBus(r.data));
    api.get("/settings/service-types").then((r) => setSts(r.data));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { bus, sts, reload };
};

const ChecklistsTab = ({ bus, sts }) => {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get("/settings/checklists").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const stName = (c) => (c ? sts.find((s) => s.code === c)?.name || c : "General");
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">El checklist se asigna por unidad de negocio y tipo de servicio. Si no existe uno específico, se usa el general de la unidad.</p>
        <Button size="sm" onClick={() => setDlg({ open: true })} data-testid="checklist-new-button"><Plus className="mr-1 h-3.5 w-3.5" />Nuevo</Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {items.map((t) => (
          <button key={t.id} onClick={() => setDlg({ open: true, template: t })} className="rounded-xl border border-border bg-card/40 p-4 text-left transition-colors hover:border-primary/40" data-testid={`checklist-card-${t.id}`}>
            <div className="flex items-center justify-between"><span className="font-medium">{t.name}</span><span className="font-mono text-[10px] text-muted-foreground">v{t.version}</span></div>
            <div className="mt-1 text-xs text-muted-foreground">{bus.find((b) => b.code === t.business_unit_code)?.name} · {stName(t.service_type_code)} · {t.items.length} puntos {!t.active && "· inactivo"}</div>
          </button>
        ))}
      </div>
      <ChecklistEditor open={!!dlg?.open} onOpenChange={(o) => !o && setDlg(null)} template={dlg?.template} businessUnits={bus} serviceTypes={sts} onSaved={load} />
    </div>
  );
};

const opt = (arr, key = "code", label = "name") => arr.map((x) => ({ value: x[key], label: x[label] }));

export default function Settings() {
  const { meta, loadMeta } = useAuth();
  const { bus, sts, reload } = useLookups();
  const buName = (c) => bus.find((b) => b.code === c)?.name || c;
  const stName = (c) => (c ? sts.find((s) => s.code === c)?.name || c : "Cualquiera");
  const envName = (c) => (c ? meta?.environments?.find((e) => e.code === c)?.label || c : "Cualquiera");
  const refresh = () => { reload(); loadMeta(); };
  const activeCol = { label: "Estado", render: (x) => (x.active ? "Activo" : "Inactivo") };
  const buField = { name: "business_unit_code", label: "Unidad de negocio", type: "select", options: opt(bus) };
  const stField = { name: "service_type_code", label: "Tipo de servicio", type: "select", nullable: true, options: opt(sts) };

  return (
    <div>
      <PageHeader eyebrow="Administración" title="Configuración" />
      <Tabs defaultValue="empresa">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start">
          {[["empresa", "Empresa"], ["catalogos", "Unidades y servicios"], ["checklists", "Checklists"], ["garantia", "Garantía"], ["mantenimiento", "Mantenimiento"], ["zonas", "Zonas"], ["especialidades", "Especialidades"], ["preparacion", "Preparación"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`settings-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="empresa"><TenantTab /></TabsContent>
        <TabsContent value="catalogos" className="grid gap-8 xl:grid-cols-2">
          <CrudTable title="Unidades de negocio" endpoint="/settings/business-units" testid="bu" onChange={refresh}
            fields={[{ name: "code", label: "Código", required: true, hidden: (_, i) => !!i }, { name: "name", label: "Nombre", required: true }, { name: "color", label: "Color", default: "#F59E0B" }, { name: "order", label: "Orden", type: "number", default: 0 }, { name: "active", label: "Activo", type: "switch" }]}
            columns={[{ label: "Nombre", render: (x) => <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: x.color }} />{x.name}</span> }, { label: "Código", render: (x) => <span className="font-mono text-xs">{x.code}</span> }, activeCol]} />
          <CrudTable title="Tipos de servicio" endpoint="/settings/service-types" testid="st" onChange={refresh}
            fields={[{ name: "code", label: "Código", required: true, hidden: (_, i) => !!i }, { name: "name", label: "Nombre", required: true }, { name: "order", label: "Orden", type: "number", default: 0 }, { name: "active", label: "Activo", type: "switch" }]}
            columns={[{ label: "Nombre", render: (x) => x.name }, { label: "Código", render: (x) => <span className="font-mono text-xs">{x.code}</span> }, activeCol]} />
        </TabsContent>
        <TabsContent value="checklists"><ChecklistsTab bus={bus} sts={sts} /></TabsContent>
        <TabsContent value="garantia">
          <CrudTable title="Políticas de garantía" endpoint="/settings/warranty-policies" testid="warranty"
            fields={[buField, stField, { name: "months", label: "Plazo (meses) — vacío si no aplica", type: "number" }, { name: "conditions", label: "Condiciones", type: "textarea" }, { name: "notes", label: "Notas internas" }, { name: "active", label: "Activa", type: "switch" }]}
            columns={[{ label: "Unidad", render: (x) => buName(x.business_unit_code) }, { label: "Servicio", render: (x) => stName(x.service_type_code) }, { label: "Plazo", render: (x) => (x.months ? `${x.months} meses` : <span className="text-amber-400">Por configurar</span>) }, { label: "Versión", render: (x) => `v${x.version}` }, activeCol]} />
        </TabsContent>
        <TabsContent value="mantenimiento">
          <CrudTable title="Políticas de mantenimiento" endpoint="/settings/maintenance-policies" testid="maintenance"
            fields={[buField, stField, { name: "environment", label: "Ambiente", type: "select", nullable: true, options: (meta?.environments || []).map((e) => ({ value: e.code, label: e.label })) },
              { name: "first_months", label: "Primer mantenimiento tras instalación (meses)", type: "number" }, { name: "periodicity_months", label: "Periodicidad posterior (meses)", type: "number" }, { name: "reason", label: "Motivo / política", type: "textarea" }, { name: "active", label: "Activa", type: "switch" }]}
            columns={[{ label: "Unidad", render: (x) => buName(x.business_unit_code) }, { label: "Servicio", render: (x) => stName(x.service_type_code) }, { label: "Ambiente", render: (x) => envName(x.environment) }, { label: "Primero", render: (x) => (x.first_months ? `${x.first_months} m` : "—") }, { label: "Periodicidad", render: (x) => (x.periodicity_months ? `${x.periodicity_months} m` : <span className="text-amber-400">Por configurar</span>) }, { label: "Versión", render: (x) => `v${x.version}` }, activeCol]} />
        </TabsContent>
        <TabsContent value="zonas"><ZonesTab /></TabsContent>
        <TabsContent value="preparacion"><PrepSettingsTab /></TabsContent>
        <TabsContent value="especialidades">
          <CrudTable title="Especialidades de técnicos" endpoint="/specialties" testid="specialty"
            fields={[{ name: "code", label: "Código", required: true, hidden: (_, i) => !!i }, { name: "name", label: "Nombre", required: true },
              { name: "kind", label: "Tipo", type: "select", default: "activity", options: [{ value: "unit", label: "Por unidad de servicio" }, { value: "activity", label: "Por actividad" }] },
              { name: "business_unit_code", label: "Unidad de negocio relacionada (tipo unidad)", type: "select", nullable: true, noneLabel: "Ninguna", options: opt(bus) },
              { name: "service_type_codes", label: "Tipos de servicio compatibles (tipo actividad)", type: "multicheck", options: opt(sts) },
              { name: "order", label: "Orden", type: "number", default: 100 }, { name: "active", label: "Activa", type: "switch" }]}
            columns={[{ label: "Nombre", render: (x) => x.name }, { label: "Tipo", render: (x) => (x.kind === "unit" ? "Unidad" : "Actividad") },
              { label: "Compatibilidad", render: (x) => (x.kind === "unit" ? buName(x.business_unit_code) || "—" : (x.service_type_codes || []).map(stName).join(", ") || "—") }, activeCol]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
